import type { CalEvent, Calendar, EventInput, EventKind, Recurrence, Settings } from "../api/types";
import { addDays, datePart, makeDateTime, minutesOf, type DateStr } from "./date";

/** Editable form state of an event (quick popover and full editor). */
export interface Draft {
  title: string;
  description: string;
  location: string;
  calendarId: string;
  allDay: boolean;
  startDate: DateStr;
  startMin: number;
  /** Last day (inclusive) for all-day events; end day for timed ones. */
  endDate: DateStr;
  endMin: number;
  recurrence: Recurrence | null;
  color: string | null;
  reminders: number[];
  kind: EventKind;
  category: string | null;
}

/** Order and labels of the kind tabs (quick create and editor). */
export const KINDS: Array<{ kind: EventKind; label: string }> = [
  { kind: "event", label: "Evento" },
  { kind: "task", label: "Tarefa" },
  { kind: "reminder", label: "Lembrete" }
];

export function newDraft(opts: {
  date: DateStr;
  startMin?: number;
  endMin?: number;
  endDate?: DateStr;
  allDay?: boolean;
  settings: Settings;
  calendars: Calendar[];
  kind?: EventKind;
}): Draft {
  const allDay = Boolean(opts.allDay);
  const startMin = opts.startMin ?? 9 * 60;
  const endMin = opts.endMin ?? startMin + opts.settings.defaultDurationMinutes;
  const cal = opts.calendars.find((c) => c.isDefault) ?? opts.calendars[0];
  return {
    title: "",
    description: "",
    location: "",
    calendarId: cal?.id ?? "",
    allDay,
    startDate: opts.date,
    startMin,
    endDate: opts.endDate ?? (endMin > 1440 ? addDays(opts.date, 1) : opts.date),
    endMin: endMin > 1440 ? endMin - 1440 : endMin,
    recurrence: null,
    color: null,
    reminders: allDay ? opts.settings.defaultAllDayReminders : opts.settings.defaultReminders,
    kind: opts.kind ?? "event",
    category: null
  };
}

export function draftFromEvent(e: CalEvent): Draft {
  return {
    title: e.title,
    description: e.description ?? "",
    location: e.location ?? "",
    calendarId: e.calendarId ?? "",
    allDay: e.allDay,
    startDate: datePart(e.start),
    startMin: minutesOf(e.start),
    // All-day ends are exclusive in the API; the form shows the last day.
    endDate: e.allDay ? addDays(datePart(e.end), -1) : datePart(e.end),
    endMin: minutesOf(e.end),
    recurrence: e.recurrence,
    color: e.eventColor,
    reminders: e.reminders,
    kind: e.kind,
    category: e.category
  };
}

export function draftToInput(d: Draft): EventInput {
  return {
    title: d.title,
    description: d.description || null,
    location: d.location || null,
    calendarId: d.calendarId || undefined,
    allDay: d.allDay,
    start: d.allDay ? d.startDate : makeDateTime(d.startDate, d.startMin),
    end: d.allDay ? addDays(d.endDate, 1) : makeDateTime(d.endDate, d.endMin),
    recurrence: d.recurrence,
    color: d.color,
    reminders: d.reminders,
    kind: d.kind,
    category: d.category
  };
}

/** Keeps the end after the start, preserving the duration when the start moves. */
export function withStart(d: Draft, date: DateStr, minutes: number): Draft {
  const duration = (d.endDate === d.startDate ? 0 : (new Date(d.endDate).getTime() - new Date(d.startDate).getTime()) / 60_000) + d.endMin - d.startMin;
  const endTotal = minutes + Math.max(duration, d.allDay ? 0 : 15);
  return {
    ...d,
    startDate: date,
    startMin: minutes,
    endDate: d.allDay ? addDays(date, Math.max(0, Math.round((new Date(d.endDate).getTime() - new Date(d.startDate).getTime()) / 86_400_000))) : addDays(date, Math.floor(endTotal / 1440)),
    endMin: ((endTotal % 1440) + 1440) % 1440
  };
}
